// Only import the Lightbulb icon as a fallback
import {Lightbulb} from 'lucide-vue-next';

// Create a registry for dynamically loaded icons
const iconRegistry = {};

// Initialize with the Lightbulb icon as fallback
iconRegistry['lightbulb'] = Lightbulb;

// For tracking loaded icons
const loadedIcons = ['lightbulb'];

// Keep track of loading promises to avoid duplicate requests
const loadingIcons = {};

/**
 * Dynamically load an icon by name
 * @param {string} name - Icon name in kebab-case (e.g., 'file-text')
 * @returns {Promise<Component>} - The icon component
 */
async function loadIcon(name) {
    // If icon is already loaded, return it
    if (iconRegistry[name]) {
        return iconRegistry[name];
    }

    // If icon is already being loaded, return the existing promise
    if (loadingIcons[name]) {
        return loadingIcons[name];
    }

    // Convert kebab-case to PascalCase for import
    const pascalName = name
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');

    try {
        // Create a loading promise
        loadingIcons[name] = import('lucide-vue-next')
            .then(module => {
                // Check if the icon exists in the module
                if (module[pascalName]) {
                    // Register the icon
                    iconRegistry[name] = module[pascalName];
                    loadedIcons.push(name);
                    return module[pascalName];
                } else {
                    // console.warn(`Icon not found: ${name}, using Lightbulb as fallback`);
                    return iconRegistry['lightbulb'];
                }
            })
            .catch(error => {
                // console.error(`Failed to load icon: ${name}`, error);
                return iconRegistry['lightbulb'];
            })
            .finally(() => {
                // Clean up the loading promise
                delete loadingIcons[name];
            });

        return loadingIcons[name];
    } catch (error) {
        console.error(`Error loading icon: ${name}`, error);
        return iconRegistry['lightbulb'];
    }
}

/**
 * Get an icon component by its name, loading it dynamically if needed
 * @param {string} name - The name of the icon in kebab-case
 * @returns {Promise<Component>} - The icon component or Lightbulb as fallback
 */
export async function getIcon(name) {
    return loadIcon(name);
}

/**
 * Check if an icon exists in the registry
 * @param {string} name - The name of the icon in kebab-case
 * @returns {boolean} - Whether the icon is already loaded
 */
export function hasIcon(name) {
    return !!iconRegistry[name];
}

/**
 * Get a list of all loaded icons
 * @returns {string[]} - Array of loaded icon names
 */
export function getLoadedIcons() {
    return [...loadedIcons];
}

// Export the registry for direct access if needed
export default iconRegistry;
